import React, { useState, useEffect } from 'react';
import {
    CheckCircle2,
    AlertCircle,
    Loader2,
    ShieldCheck,
    Wallet,
    ChevronDown,
    ChevronUp,
    Send,
    UserCheck,
    Building2,
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import {
    client,
    currencyCodeToSymbolMap,
    fetchPocketBalance,
    fetchStoresWhereStaffAssigned,
    fetchPayrollAndPayslips
} from '../apiHelper';
import { useToast } from '../hooks/useToast';

export const PayrollCheckerManager = ({ storeId }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submittingId, setSubmittingId] = useState(null);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(storeId || '');
    const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
    const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);

    const [payrollRuns, setPayrollRuns] = useState([]);
    const [walletBalance, setWalletBalance] = useState(0);
    const [expandedRunId, setExpandedRunId] = useState(null);

    const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
    const [otpValue, setOtpValue] = useState('');
    const [otpLoading, setOtpLoading] = useState(false);
    const [pendingDisbursement, setPendingDisbursement] = useState(null);

    const activeStore = stores.find(s => Number(s.value) === Number(selectedStoreId));
    const currencySymbol = currencyCodeToSymbolMap[activeStore?.currency] || '₦';

    const [selectedPayslips, setSelectedPayslips] = useState({});

    // 1. Fetch stores on mount
    useEffect(() => {
        const loadStores = async () => {
            try {
                const stored = localStorage.getItem('user');
                const parsed = stored ? JSON.parse(stored) : null;
                if (parsed) {
                    const returnedStores = await fetchStoresWhereStaffAssigned(parsed);
                    const filtered = returnedStores.filter(s => s.storetype !== 'WHS');
                    const mapped = filtered.map(s => ({
                        label: s.identifier,
                        value: s.store_id,
                        currency: s.setccurr
                    }));
                    setStores(mapped);
                    if (!selectedStoreId && mapped.length > 0) {
                        setSelectedStoreId(mapped[0].value);
                    }
                }
            } catch (err) {
                showToast("Failed to load store context.", "error");
            }
        };
        loadStores();
    }, []);

    // 2. Fetch payroll runs and wallet balance on parameter change
    const loadPayrollData = async () => {
        if (!selectedStoreId) return;
        setLoading(true);
        try {
            // Fetch wallet balance
            const walletRes = await fetchPocketBalance(selectedStoreId);
            setWalletBalance(walletRes?.balance_amount || 0);

            // Fetch payroll runs & payslips
            const paramObj = {
                store_id: Number(selectedStoreId),
                period_year: Number(periodYear),
                period_month: Number(periodMonth)
            };
            const runsData = await fetchPayrollAndPayslips(paramObj);
            // console.log(runsData)
            setPayrollRuns(Array.isArray(runsData) ? runsData : []);

            // Auto-expand first run if available
            if (runsData && runsData.length > 0) {
                setExpandedRunId(runsData[0].payrollrun_id);
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to load payroll records.", "error");
            setPayrollRuns([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedStoreId) {
            loadPayrollData();
        }
    }, [selectedStoreId, periodYear, periodMonth]);

    useEffect(() => {
        if (payrollRuns.length > 0) {
            const initialSelections = {};
            payrollRuns.forEach((run) => {
                // Pre-select all eligible (pending) payslips by default
                const pendingIds = (run.payslips || [])
                    .filter(ps => ps.disbursement_status !== 'successful' && ps.disbursement_status !== 'paid')
                    .map(ps => ps.payslip_id);
                initialSelections[run.payrollrun_id] = pendingIds;
            });
            setSelectedPayslips(initialSelections);
        }
    }, [payrollRuns]);

    // // 3. Approve and Disburse Payroll Run
    // const handleApproveAndDisburse = async (run) => {
    //     const totalNet = Number(run.total_net || 0);
    //     if (walletBalance < totalNet) {
    //         showToast("Insufficient wallet balance to approve and disburse this payroll run.", "error");
    //         return;
    //     }

    //     setSubmittingId(run.payrollrun_id);
    //     try {
    //         const payload = {
    //             payrollrun_id: run.payrollrun_id,
    //             store_id: Number(selectedStoreId)
    //         };

    //         await client.postData('/payroll/approve-and-disburse', payload);
    //         showToast(`Payroll Run #${run.payrollrun_id} successfully approved and disbursed!`, "success");
    //         loadPayrollData();
    //     } catch (err) {
    //         showToast(err.response?.data?.detail || "Failed to disburse payroll run.", "error");
    //     } finally {
    //         setSubmittingId(null);
    //     }
    // };

    const handleApproveAndDisburse = async (run) => {
        const runId = run.payrollrun_id;
        const selectedIds = selectedPayslips[runId] || [];

        if (selectedIds.length === 0) {
            showToast("Please select at least one payslip for disbursement.", "error");
            return;
        }

        // Calculate total net for SELECTED payslips only (accounting for deductions)
        const selectedNetTotal = (run.payslips || [])
            .filter((ps) => selectedIds.includes(ps.payslip_id))
            .reduce((sum, ps) => {
                const gross = Number(ps.gross_salary || 0);
                const paye = Number(ps.paye_amount || 0);
                const deductions = Number(ps.other_deductions || 0);
                const computedNet = Number(ps.net_salary || (gross - paye - deductions));
                return sum + computedNet;
            }, 0);

        if (walletBalance < selectedNetTotal) {
            showToast("Insufficient wallet balance for selected payslips.", "error");
            return;
        }

        setSubmittingId(runId);
        try {
            const payload = {
                payrollrun_id: runId,
                store_id: Number(selectedStoreId),
                payslip_ids: selectedIds
            };

            const res = await client.postData('/payroll/approve-and-disburse', payload);

            // extract payout_id and store pending disbursement details
            const payoutId = res?.payout_id || res?.data?.payout_id;
            const successMsg = res?.msg || res?.data?.msg || "OTP sent to registered pocket email.";

            if (payoutId) {
                setPendingDisbursement({
                    payout_id: payoutId,
                    payrollrun_id: runId,
                    store_id: Number(selectedStoreId),
                })
                setIsOtpModalOpen(true);
                showToast(successMsg, "info");
            } else {
                showToast("Disbursement initiated. but no payout ID was returned", "error");
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to disburse payroll run.", "error");
        } finally {
            setSubmittingId(null);
        }
    };

    const handleConfirmOtp = async (e) => {
        e.preventDefault();
        if (!otpValue) {
            showToast("Please enter the OTP sent to your email.", "error");
            return;
        }

        setOtpLoading(true);
        try {
            const payload = {
                payout_id: pendingDisbursement.payout_id,
                otp: otpValue,
                store_id: pendingDisbursement.store_id,
                payrollrun_id: pendingDisbursement.payrollrun_id
            };

            await client.postData('/payroll/confirm-payout', payload);
            showToast("Payout successfully authorized and dispatched for processing!", "success");

            setIsOtpModalOpen(false);
            setOtpValue('');
            setPendingDisbursement(null);
            loadPayrollData();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to authorize payout with provided OTP.", "error");
        } finally {
            setOtpLoading(false);
        }
    };

    const toggleExpand = (id) => {
        setExpandedRunId(prev => prev === id ? null : id);
    };

    const formatCurr = (val) => `${currencySymbol}${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    // Toggle individual payslip selection
    const handleTogglePayslip = (runId, payslipId) => {
        setSelectedPayslips((prev) => {
            const currentList = prev[runId] || [];
            const exists = currentList.includes(payslipId);
            const updated = exists
                ? currentList.filter((id) => id !== payslipId)
                : [...currentList, payslipId];
            return { ...prev, [runId]: updated };
        });
    };

    // Select All / Deselect All for a specific run
    const handleToggleAllPayslips = (run) => {
        const runId = run.payrollrun_id;
        const eligiblePayslips = (run.payslips || []).filter(
            (ps) => ps.disbursement_status !== 'successful' && ps.disbursement_status !== 'paid'
        );
        const currentSelections = selectedPayslips[runId] || [];

        if (currentSelections.length === eligiblePayslips.length) {
            // Deselect all
            setSelectedPayslips((prev) => ({ ...prev, [runId]: [] }));
        } else {
            // Select all eligible
            setSelectedPayslips((prev) => ({
                ...prev,
                [runId]: eligiblePayslips.map((ps) => ps.payslip_id),
            }));
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
            <Card className="p-6 bg-white border-zinc-200 shadow-sm">
                {/* Header Controls */}
                <div className="border-b border-zinc-100 pb-4 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                            <ShieldCheck size={20} className="text-emerald-600" /> Payroll Approval & Disbursement (Checker)
                        </h2>
                        <p className="text-xs text-zinc-500">
                            Review pending payroll runs, inspect employee payslips, and authorize payout disbursements.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <select
                            value={selectedStoreId}
                            onChange={(e) => setSelectedStoreId(e.target.value)}
                            className="bg-zinc-50 border border-zinc-200 text-xs rounded-lg px-2.5 py-2 font-medium"
                        >
                            {stores.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>

                        <select
                            value={periodMonth}
                            onChange={(e) => setPeriodMonth(e.target.value)}
                            className="bg-zinc-50 border border-zinc-200 text-xs rounded-lg px-2.5 py-2 font-medium"
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                </option>
                            ))}
                        </select>

                        <input
                            type="number"
                            value={periodYear}
                            onChange={(e) => setPeriodYear(e.target.value)}
                            className="w-20 bg-zinc-50 border border-zinc-200 text-xs rounded-lg px-2.5 py-2 font-medium"
                        />
                    </div>
                </div>

                {/* Wallet Balance Indicator */}
                <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-xl mb-6 flex justify-between items-center text-xs">
                    <div className="flex items-center gap-2 text-zinc-600 font-medium">
                        <Wallet size={16} className="text-emerald-600" />
                        <span>Wallet Balance Available:</span>
                        <strong className="text-zinc-900 font-mono text-sm">{formatCurr(walletBalance)}</strong>
                    </div>
                    <span className="text-[11px] text-zinc-500 font-medium">
                        Operations Context: <strong className="text-zinc-800">{activeStore?.label || 'Select Store'}</strong>
                    </span>
                </div>

                {/* Payroll Runs Section */}
                <div className="space-y-4">
                    <h3 className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                        Pending Approval Requests
                    </h3>

                    {loading ? (
                        <div className="p-8 text-center text-xs text-zinc-500">
                            <Loader2 size={16} className="animate-spin inline mr-2 text-emerald-600" />
                            Fetching payroll records...
                        </div>
                    ) : payrollRuns.length === 0 ? (
                        <div className="p-8 border border-dashed border-zinc-200 rounded-xl text-center text-xs text-zinc-400">
                            No payroll runs found for the selected store and period.
                        </div>
                    ) : (
                        payrollRuns.map((run) => {
                            const isPending = run.status === 'pending_approval';
                            const isDisbursed = run.status === 'completed' || run.status === 'disbursed';
                            const totalNetNum = Number(run.total_net || 0);
                            const hasInsufficient = walletBalance < totalNetNum;
                            const isExpanded = expandedRunId === run.payrollrun_id;

                            return (
                                <div key={run.payrollrun_id} className="border border-zinc-200 rounded-xl bg-white overflow-hidden shadow-xs">
                                    {/* Summary Row */}
                                    <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-zinc-50/40 border-b border-zinc-100">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-zinc-800 text-sm">
                                                    Run #{run.payrollrun_id}
                                                </span>
                                                {/* Status Badge */}
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border capitalize ${isDisbursed
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                    : isPending
                                                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                        : 'bg-zinc-100 text-zinc-600 border-zinc-200'
                                                    }`}>
                                                    {run.status?.replace('_', ' ')}
                                                </span>
                                            </div>

                                            {/* Organization & Creator Context */}
                                            <div className="flex items-center gap-2 text-xs text-zinc-600">
                                                <span className="flex items-center gap-1 font-medium text-zinc-800">
                                                    <Building2 size={13} className="text-zinc-400" />
                                                    {run.organization_name || run.created_by_name || 'Organization Payroll'}
                                                </span>
                                            </div>

                                            {/* Meta Info */}
                                            <p className="text-[11px] text-zinc-400">
                                                Period: <strong className="font-mono text-zinc-600">{run.period_label}</strong> |
                                                Employees: <strong className="font-mono text-zinc-600">{run.total_employees}</strong> |
                                                Created: <span className="font-mono text-zinc-500">{run.payroll_created_on ? new Date(run.payroll_created_on).toLocaleDateString() : 'N/A'}</span>
                                            </p>
                                        </div>

                                        {/* Figures */}
                                        <div className="grid grid-cols-4 gap-3 text-right text-xs">
                                            <div>
                                                <span className="text-zinc-400 block text-[10px]">Total Gross</span>
                                                <span className="font-mono font-semibold text-zinc-800">{formatCurr(run.total_gross)}</span>
                                            </div>
                                            <div>
                                                <span className="text-zinc-400 block text-[10px]">Total PAYE</span>
                                                <span className="font-mono font-semibold text-amber-600">{formatCurr(run.total_paye)}</span>
                                            </div>
                                            <div>
                                                <span className="text-zinc-400 block text-[10px]">Deductions</span>
                                                <span className="font-mono font-semibold text-red-600">
                                                    {formatCurr(
                                                        (run.payslips || []).reduce((sum, p) => sum + Number(p.other_deductions || 0), 0)
                                                    )}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-zinc-400 block text-[10px]">Net Payout</span>
                                                <span className="font-mono font-bold text-emerald-700">{formatCurr(run.total_net)}</span>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2 justify-end pt-2 md:pt-0 border-t md:border-t-0 border-zinc-100">
                                            <Button
                                                onClick={() => toggleExpand(run.payrollrun_id)}
                                                variant="ghost"
                                                className="text-xs px-2.5 py-1.5 flex items-center gap-1 text-zinc-600 hover:bg-zinc-100"
                                            >
                                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                {isExpanded ? 'Hide Details' : 'Inspect Details'}
                                            </Button>

                                            {isPending && (
                                                <Button
                                                    onClick={() => handleApproveAndDisburse(run)}
                                                    disabled={submittingId === run.payrollrun_id || hasInsufficient}
                                                    className={`${hasInsufficient
                                                        ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                                                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                                        } text-xs px-3 py-1.5 flex items-center gap-1.5 font-medium`}
                                                >
                                                    {submittingId === run.payrollrun_id ? (
                                                        <Loader2 size={14} className="animate-spin" />
                                                    ) : (
                                                        <Send size={13} />
                                                    )}
                                                    Approve & Disburse
                                                </Button>
                                            )}

                                            {isDisbursed && (
                                                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 px-2 py-1">
                                                    <CheckCircle2 size={14} /> Disbursed
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Insufficient Funds Warning */}
                                    {isPending && hasInsufficient && (
                                        <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-[11px] text-red-600 flex items-center gap-1.5">
                                            <AlertCircle size={13} />
                                            Insufficient wallet funds to disburse. Required: {formatCurr(totalNetNum)}, Available: {formatCurr(walletBalance)}.
                                        </div>
                                    )}

                                    {/* Expandable Payslips Table with Full Details */}
                                    {isExpanded && (
                                        <div className="p-4 bg-white space-y-3">
                                            {(() => {
                                                const runSelected = selectedPayslips[run.payrollrun_id] || [];
                                                const eligiblePayslips = (run.payslips || []).filter(
                                                    ps => ps.disbursement_status !== 'success' && ps.disbursement_status !== 'paid'
                                                );
                                                const isAllSelected = eligiblePayslips.length > 0 && runSelected.length === eligiblePayslips.length;

                                                const selectedNetTotal = (run.payslips || [])
                                                    .filter(ps => runSelected.includes(ps.payslip_id))
                                                    .reduce((sum, ps) => {
                                                        const gross = Number(ps.gross_salary || 0);
                                                        const paye = Number(ps.paye_amount || 0);
                                                        const deductions = Number(ps.other_deductions || 0);
                                                        return sum + (gross - paye - deductions);
                                                    }, 0);

                                                return (
                                                    <>
                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-zinc-100 pb-2">
                                                            <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide flex items-center gap-1">
                                                                <UserCheck size={13} /> Payslip Breakdown ({runSelected.length}/{eligiblePayslips.length} Selected)
                                                            </h4>
                                                            <div className="text-xs text-zinc-600 font-medium">
                                                                Selected Payout: <strong className="text-emerald-700 font-mono">{formatCurr(selectedNetTotal)}</strong>
                                                            </div>
                                                        </div>

                                                        {!run.payslips || run.payslips.length === 0 ? (
                                                            <p className="text-xs text-zinc-400 italic">No individual payslips found for this run.</p>
                                                        ) : (
                                                            <div className="border border-zinc-200 rounded-lg overflow-x-auto">
                                                                <table className="w-full text-left text-xs">
                                                                    <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-sans font-semibold">
                                                                        <tr>
                                                                            <th className="p-2.5 w-10 text-center">
                                                                                {isPending && eligiblePayslips.length > 0 && (
                                                                                    <Checkbox
                                                                                        checked={isAllSelected}
                                                                                        onChange={() => handleToggleAllPayslips(run)}
                                                                                        containerClassName="mb-0 flex justify-center"
                                                                                    />
                                                                                )}
                                                                            </th>
                                                                            <th className="p-2.5">Employee</th>
                                                                            <th className="p-2.5">Bank Details</th>
                                                                            <th className="p-2.5">Branch</th>
                                                                            <th className="p-2.5 text-right">Gross</th>
                                                                            <th className="p-2.5 text-right">PAYE</th>
                                                                            <th className="p-2.5 text-right">Deductions</th>
                                                                            <th className="p-2.5 text-right">Net Pay</th>
                                                                            <th className="p-2.5 text-center">Status</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-zinc-100 font-mono">
                                                                        {run.payslips.map((ps) => {
                                                                            const isAlreadyDisbursed = ps.disbursement_status === 'success' || ps.disbursement_status === 'paid';
                                                                            const isChecked = runSelected.includes(ps.payslip_id);
                                                                            const gross = Number(ps.gross_salary || 0);
                                                                            const paye = Number(ps.paye_amount || 0);
                                                                            const deductions = Number(ps.other_deductions || 0);
                                                                            const netPay = gross - paye - deductions;

                                                                            return (
                                                                                <tr key={ps.payslip_id} className={`hover:bg-zinc-50/50 ${isChecked ? 'bg-emerald-50/20' : ''}`}>
                                                                                    {/* Individual Row Checkbox */}
                                                                                    <td className="p-2.5 text-center">
                                                                                        {!isAlreadyDisbursed && isPending ? (
                                                                                            <Checkbox
                                                                                                checked={isChecked}
                                                                                                onChange={() => handleTogglePayslip(run.payrollrun_id, ps.payslip_id)}
                                                                                                containerClassName="mb-0 flex justify-center"
                                                                                            />
                                                                                        ) : (
                                                                                            <span className="text-zinc-300">-</span>
                                                                                        )}
                                                                                    </td>

                                                                                    {/* Employee Name & ID */}
                                                                                    <td className="p-2.5 font-sans">
                                                                                        <div className="font-semibold text-zinc-900">{ps.employee_name}</div>
                                                                                        <div className="text-[10px] text-zinc-400 font-mono">ID: #{ps.member_id}</div>
                                                                                    </td>

                                                                                    {/* Bank & Account Number */}
                                                                                    <td className="p-2.5">
                                                                                        <div className="font-medium text-zinc-800">{ps.bank_name || 'N/A'}</div>
                                                                                        <div className="text-[10px] text-zinc-500 font-mono">{ps.account_number}</div>
                                                                                    </td>

                                                                                    {/* Branch */}
                                                                                    <td className="p-2.5 font-sans text-zinc-600">
                                                                                        {ps.branch_name || 'Main Branch'}
                                                                                    </td>

                                                                                    {/* Financial Figures */}
                                                                                    <td className="p-2.5 text-right text-zinc-800">{formatCurr(gross)}</td>
                                                                                    <td className="p-2.5 text-right text-amber-600">{formatCurr(paye)}</td>
                                                                                    <td className="p-2.5 text-right text-red-600">{formatCurr(deductions)}</td>
                                                                                    <td className="p-2.5 text-right font-bold text-emerald-600">{formatCurr(netPay)}</td>

                                                                                    {/* Disbursement Status */}
                                                                                    <td className="p-2.5 text-center font-sans">
                                                                                        <span className={`inline-block px-2 py-0.5 text-[10px] rounded font-medium capitalize ${isAlreadyDisbursed
                                                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                                            : ps.disbursement_status === 'failed'
                                                                                                ? 'bg-red-50 text-red-700 border border-red-200'
                                                                                                : 'bg-zinc-100 text-zinc-600 border border-zinc-200'
                                                                                            }`}>
                                                                                            {ps.disbursement_status || 'Pending'}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </Card>
            <Modal
                isOpen={isOtpModalOpen}
                onClose={() => {
                    setIsOtpModalOpen(false);
                    setOtpValue('');
                }}
                title="Enter Disburse OTP Verification"
                size="md"
            >
                <form onSubmit={handleConfirmOtp} className="space-y-4">
                    <p className="text-xs text-zinc-600">
                        An OTP has been dispatched to your registered Seerbit Pocket email address. Please enter the passcode to authorize payout release.
                    </p>

                    <Input
                        label="One-Time Password (OTP)"
                        placeholder="Enter OTP (e.g. 123456)"
                        value={otpValue}
                        onChange={(e) => setOtpValue(e.target.value)}
                        required
                    />

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setIsOtpModalOpen(false);
                                setOtpValue('');
                            }}
                            disabled={otpLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={otpLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {otpLoading ? <Loader2 size={16} className="animate-spin inline mr-1" /> : null}
                            Confirm & Authorize
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default PayrollCheckerManager;